import {Injectable} from '@angular/core';
import {HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest, HttpStatusCode} from '@angular/common/http';
import {catchError, map, Observable, throwError} from 'rxjs';
import {DataStorageService} from '../services/data-storage.service';
import {AuthenticationService} from '../services/authentication.service';
import {Router} from '@angular/router';
import {environment} from '../../environments/environment';
import {AlertsService, CLToastType, ModalService} from '@clavisco/alerts';
import {MessageError} from "../interfaces/token";

@Injectable()
export class AppInterceptor implements HttpInterceptor {
  constructor (
    private dataStorageService: DataStorageService,
    private authService: AuthenticationService,
    private router: Router,
    private alertService: AlertsService,
    private modalService: ModalService
  ) {
  }

  readonly UnauthorizedMessage: string[] = [
    'Su sesión pudo haber expirado, intente iniciando nuevamente',
    'Datos de licencia de sap incorrectos',
    'No cuenta con permisos para realizar esta acción'
  ];

  /**
   * Intercepts HTTP requests and responses to handle authentication and error scenarios.
   *
   * This method performs the following actions:
   * - Allows requests with URLs containing 'assets', 'token', 'ChangeRecoverPassword', 'RecoveryPassword', or 'ChangePassword' to pass through unmodified.
   * - For these requests, catches unauthorized errors and returns the error message if the status code is `401 Unauthorized`.
   * - For other requests, adds authentication headers, sets the content type to 'application/json' if not already present, and prepends the API URL to the request URL.
   * - Handles errors by showing an appropriate message to the user, either via a modal or a toast notification, based on the error type.
   *
   * @param req - The outgoing HTTP request being intercepted.
   * @param next - The HTTP handler that will handle the request after interception.
   * @returns An observable of the HTTP event that completes with the HTTP response or error.
   */
  intercept (req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (req.url.includes('assets') || req.url.includes('token') || req.url.includes('ChangeRecoverPassword') || req.url.includes('RecoveryPassword') || req.url.includes('ChangePassword')) {
      return next.handle(req).pipe(
        catchError((error: any) => {
          return throwError(() => {
            if (error?.error?.code === HttpStatusCode.Unauthorized) {
              return error = error.error.message;
            }
            return error;
          });
        }));
    }

    req = this.AddAuthentication(req);
    let clonedRequest: HttpRequest<unknown> = req.clone();

    // Valido que no tenga el header que indica si tiene archivos para no enviar el content type
    if (!clonedRequest.headers.has('Request-With-Files')) {
      let headers = clonedRequest.headers.set('Content-Type', 'application/json');
      clonedRequest = clonedRequest.clone({headers});
    }
    clonedRequest = clonedRequest.clone({url: `${environment.ApiUrl}${req.url}`});

    return next.handle(clonedRequest).pipe(
      map((event: HttpEvent<any>) => {
        return event;
      }),
      catchError((error: any) => {
        const messageError: MessageError = this.ShowMessageError(error);
        if(messageError.InModal){
          this.modalService.NextError(
            {
              title: messageError.Message
            }
          )
        } else {
          this.alertService.Toast({message:decodeURIComponent(messageError.Message) , type: CLToastType.ERROR});
        }
        throw new Error(messageError.Message);
      })
    );
  }

  /**
   * Generates a user-friendly error message based on the HTTP error status.
   *
   * @param error - The error object containing details of the HTTP error.
   * @returns A `MessageError` object containing the error message and a flag indicating whether the message should be displayed in a modal.
   */
  ShowMessageError (error: any): MessageError {
    var message: string = '';
    let messageError: MessageError = { Message: "", InModal: false};
    switch (error['status']) {
      case 0:
        message = 'Sin conexión con el servidor';
        break;
      case HttpStatusCode.Forbidden:
        message = 'No tiene permiso para realizar la petición';
        break;
      case HttpStatusCode.PayloadTooLarge:
        message = 'El archivo enviado supera el tamaño máximo permitido por el servidor';
        break;
      case HttpStatusCode.InternalServerError:
        if (error.hasOwnProperty('error') && error.error.hasOwnProperty('Message')) {
          message = error.error.Message;
        } else {
          message = error['error'];
        }
        break;
      case HttpStatusCode.Unauthorized:
        message = this.UnauthorizedMessage.join(', ');
        messageError.InModal = true
        if (!this.authService.IsActhenticated()) {
          this.authService.Logout();
          this.router.navigateByUrl('/Login');
        }
        break;
      default:
        if (error.hasOwnProperty('error') && error.error.hasOwnProperty('Message')) {
          message = error.error?.Message;
        } else {
          message = error.error?.title;
        }
        break;
    }
    messageError.Message = message;
    return messageError;
  }

  /**
   * Adds an authentication token to the HTTP request headers if the user is authenticated.
   *
   * @param request - The HTTP request to which the authentication token should be added.
   * @returns A new HTTP request with the authentication token added, or the original request if no token is available.
   */
  private AddAuthentication (request: HttpRequest<any>): HttpRequest<any> {
    const TOKEN = JSON.parse(atob(this.dataStorageService.GetStorage('CurrentSession')!));

    if (!TOKEN) {
      return request;
    }

    if (!this.authService.IsActhenticated()) {
      this.authService.Logout();
      this.router.navigateByUrl('/Login');
      throw new Error('No está autenticado, por favor inicie sesión');
    }

    let headers: HttpHeaders = request.headers
        .set(`Authorization`, `Bearer ${TOKEN.access_token}`)
        //Se requiere para el componente de wizard setup
        .set('cl-company-id', this.dataStorageService.GetStorage('CompanyId') ?? '0');

    request = request.clone({headers});
    return request;
  }
}
